import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { adminUpdateUser } from "@/lib/api";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Modal para que el admin edite nombre, email y contraseña de un usuario.
 * La contraseña solo se cambia si se rellena. El email del admin principal no es editable.
 */
export function EditUserDialog({ user, isAdminPrincipal, onClose, onSaved }) {
  const [nombre, setNombre] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    setNombre(user.nombre || "");
    setEmail(user.email || "");
    setPassword("");
  }, [user]);

  const emailChanged = !!user && email.trim().toLowerCase() !== (user.email || "").toLowerCase();

  const handleSave = async () => {
    if (!user) return;
    const body = {};
    const nombreTrim = nombre.trim();
    if (!nombreTrim) {
      toast.error("El nombre no puede estar vacío");
      return;
    }
    if (nombreTrim !== (user.nombre || "")) body.nombre = nombreTrim;
    if (emailChanged) body.email = email.trim().toLowerCase();
    if (password) {
      if (password.length < 8) {
        toast.error("La contraseña debe tener al menos 8 caracteres");
        return;
      }
      body.password = password;
    }
    if (Object.keys(body).length === 0) {
      onClose();
      return;
    }
    setSaving(true);
    try {
      await adminUpdateUser(user.email, body);
      toast.success("Usuario actualizado");
      onClose();
      await onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al actualizar usuario");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!user} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Editar usuario</DialogTitle>
          <DialogDescription>Deja la contraseña vacía para mantener la actual.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="edit-user-nombre">Nombre</Label>
            <Input id="edit-user-nombre" type="text" value={nombre} onChange={(e) => setNombre(e.target.value)} />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="edit-user-email">Email</Label>
            <Input
              id="edit-user-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isAdminPrincipal}
            />
            {isAdminPrincipal && (
              <p className="text-xs text-muted-foreground">El email del administrador principal no se puede cambiar.</p>
            )}
            {emailChanged && (
              <p className="text-xs text-amber-600">
                El usuario tendrá que volver a iniciar sesión. Los datos asociados al email anterior (archivos, correos…) no
                se migran.
              </p>
            )}
          </div>
          <div className="grid gap-2">
            <Label htmlFor="edit-user-password">Nueva contraseña</Label>
            <Input
              id="edit-user-password"
              type="password"
              placeholder="••••••••"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="button" onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
